import React, { useState } from 'react';
import { Plus, Search, Edit, Trash2, Tag } from 'lucide-react';

interface GenericItem {
  id: string;
  name: string;
  description?: string;
  color?: string;
  category?: string;
  order_index?: number;
}

interface DisplayField<T> {
  key: keyof T;
  label: string;
  type?: 'text' | 'color' | 'badge';
}

interface GenericCrudProps<T extends GenericItem> {
  title: string;
  subtitle: string;
  items: T[];
  onAdd: (item: Omit<T, 'id' | 'companyId' | 'createdAt'>) => void;
  onUpdate: (id: string, item: Partial<T>) => void;
  onDelete: (id: string) => void;
  fields: {
    key: keyof T;
    label: string;
    type: 'text' | 'textarea' | 'select' | 'color' | 'number';
    required?: boolean;
    options?: { value: string; label: string }[];
    placeholder?: string;
  }[];
  displayFields?: DisplayField<T>[];
}

function GenericCrud<T extends GenericItem>({
  title,
  subtitle,
  items,
  onAdd,
  onUpdate,
  onDelete,
  fields,
  displayFields
}: GenericCrudProps<T>) {
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<Partial<T>>({});

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingItem) {
      onUpdate(editingItem.id, formData);
    } else {
      onAdd(formData as Omit<T, 'id' | 'companyId' | 'createdAt'>);
    }
    resetForm();
  };

  const handleEdit = (item: T) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este elemento?')) {
      onDelete(id);
    }
  };

  const resetForm = () => {
    const initialData: Partial<T> = {};
    fields.forEach(field => {
      if (field.type === 'color') {
        initialData[field.key] = '#6b7280' as any;
      } else if (field.type === 'number') {
        initialData[field.key] = 0 as any;
      } else {
        initialData[field.key] = '' as any;
      }
    });
    setFormData(initialData);
    setEditingItem(null);
    setShowForm(false);
  };

  const handleInputChange = (key: keyof T, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const renderField = (field: typeof fields[0]) => {
    const value = formData[field.key] || '';

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            value={value as string}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            required={field.required}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={field.placeholder}
          />
        );
      case 'select':
        return (
          <select
            value={value as string}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            required={field.required}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Seleccionar...</option>
            {field.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      case 'color':
        return (
          <input
            type="color"
            value={value as string}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            required={field.required}
            className="w-full h-10 px-1 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={value as number}
            onChange={(e) => handleInputChange(field.key, parseInt(e.target.value) || 0)}
            required={field.required}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={field.placeholder}
          />
        );
      default:
        return (
          <input
            type="text"
            value={value as string}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            required={field.required}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={field.placeholder}
          />
        );
    }
  };

  const renderDisplayValue = (item: T, field: DisplayField<T>) => {
    const value = item[field.key];
    
    switch (field.type) {
      case 'color':
        return (
          <div className="flex items-center">
            <div 
              className="w-4 h-4 rounded-full mr-2"
              style={{ backgroundColor: value as string }}
            />
            <span>{value as string}</span>
          </div>
        );
      case 'badge':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {value as string}
          </span>
        );
      default:
        return <span>{value as string}</span>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-gray-600">{subtitle}</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nuevo
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingItem ? 'Editar' : 'Nuevo'} {title.slice(0, -1)}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {fields.map((field) => (
                <div key={field.key as string}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label} {field.required && '*'}
                  </label>
                  {renderField(field)}
                </div>
              ))}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingItem ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder={`Buscar ${title.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredItems.length > 0 ? (
          filteredItems.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center flex-1 min-w-0">
                    <div className="p-2 bg-blue-100 rounded-lg mr-3">
                      <Tag className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {item.name}
                      </h3>
                      {item.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(item)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {displayFields && displayFields.length > 0 && (
                  <div className="space-y-2">
                    {displayFields.map((field) => (
                      <div key={field.key as string} className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{field.label}:</span>
                        {renderDisplayValue(item, field)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full">
            <div className="text-center py-12">
              <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay {title.toLowerCase()}</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm ? 'No se encontraron elementos con los filtros aplicados.' : `Comienza agregando tu primer ${title.slice(0, -1).toLowerCase()}.`}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowForm(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Nuevo {title.slice(0, -1)}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GenericCrud;